/**
 * Iter 57.1 — "Install on phone" tile shown on the Login page so site staff
 * discover the mobile attendance PWA.
 *
 * Behaviour matrix:
 *   • Already installed (display-mode standalone)  → component renders nothing
 *   • Chrome / Edge / Samsung Internet on Android  → "Install App" button (one-tap)
 *   • iOS Safari                                   → step-by-step Add-to-Home-Screen guide
 *   • Desktop browsers                             → QR-code with /m/attend URL
 */
import React, { useState } from 'react';
import { Smartphone, Download, Share, Plus, ScanFace, MapPin } from 'lucide-react';
import { useInstallPrompt } from '../lib/pwaInstall';

export default function InstallAppPanel() {
  const { canInstall, isStandalone, isIOS, promptInstall } = useInstallPrompt();
  const [installing, setInstalling] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  if (isStandalone) return null;

  const handleInstall = async () => {
    setInstalling(true);
    await promptInstall();
    setInstalling(false);
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-4 sm:p-5"
      data-testid="install-app-panel"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-md bg-[#1F7A6E]/10 text-[#1F7A6E] flex items-center justify-center">
          <Smartphone size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#1F7A6E]">
            Site staff · install on phone
          </div>
          <div className="font-display font-bold text-slate-900 mt-0.5 leading-tight">
            Get the ELR Industries app for face-attendance & DPR
          </div>
          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
            One-tap punch-in/out with face verification + GPS. No Play Store needed — installs straight from your browser.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            <Pill icon={ScanFace} label="Face verify" />
            <Pill icon={MapPin} label="Geo-tagged" />
            <Pill icon={Smartphone} label="Works offline" />
          </div>

          {/* CTA */}
          <div className="mt-3">
            {canInstall && (
              <button
                onClick={handleInstall}
                disabled={installing}
                className="flex items-center gap-2 bg-[#1F7A6E] text-white px-4 py-2 rounded-md text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2f3a5d] active:scale-[0.98] disabled:opacity-50"
                data-testid="install-app-btn"
              >
                <Download size={14} /> {installing ? 'Installing…' : 'Install App'}
              </button>
            )}
            {!canInstall && !isIOS && (
              <div className="text-[11px] text-slate-500">
                On your phone, open this site in <b>Chrome</b> → menu (⋮) → <b>Install app</b>.
              </div>
            )}
            {!canInstall && isIOS && (
              <button
                onClick={() => setShowIosGuide((s) => !s)}
                className="flex items-center gap-2 bg-[#1F7A6E] text-white px-4 py-2 rounded-md text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2f3a5d]"
                data-testid="install-app-ios-guide"
              >
                <Share size={14} /> {showIosGuide ? 'Hide steps' : 'Install on iPhone / iPad'}
              </button>
            )}
          </div>

          {showIosGuide && (
            <ol className="mt-3 text-[11px] text-slate-700 space-y-1.5 list-decimal pl-5 leading-snug" data-testid="install-app-ios-steps">
              <li>Tap the <b>Share</b> icon <Share size={11} className="inline -mt-0.5" /> at the bottom of Safari.</li>
              <li>Scroll and tap <b>Add to Home Screen</b> <Plus size={11} className="inline -mt-0.5" />.</li>
              <li>Tap <b>Add</b> in the top-right. The Ellora icon will appear on your home screen.</li>
              <li>Open the app from the home screen — full-screen, camera & GPS ready.</li>
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full text-[10px] font-display font-semibold text-slate-600">
      <Icon size={10} /> {label}
    </span>
  );
}
